import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, CheckCircle2 } from "lucide-react";
import { fetchPlans } from "../lib/api";
import { summarizePlanProgress } from "../lib/readingProgress";
import { Skeleton } from "../components/ui/skeleton";
import NewsletterCTA from "../components/NewsletterCTA";

const ctaLabel = (s) => {
  if (s.completed) return "Rever plano";
  if (s.started) return "Continuar plano";
  return "Começar plano";
};

export default function Planos() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlans().then(setPlans).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-5xl mx-auto section-padding py-8" data-testid="plans-page">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-navy/60 hover:text-navy mb-6"
        data-testid="plans-back"
      >
        <ArrowLeft className="w-4 h-4" /> Início
      </Link>

      <div className="mb-10">
        <p className="text-[10px] uppercase tracking-[0.3em] text-navy/50">Jornadas espirituais</p>
        <h1 className="font-display text-4xl sm:text-5xl text-navy mt-2">Planos de Leitura</h1>
        <p className="text-navy/70 mt-3 max-w-2xl">
          Trilhas breves de reflexão pastoral para caminhar com ritmo e propósito.
        </p>
      </div>

      {loading ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-72 rounded-3xl" />)}
        </div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {plans.map((p) => {
            const summary = summarizePlanProgress(p.slug, p.duration_days);
            const targetHref = summary.started ? (summary.last_href || `/planos/${p.slug}/dia/${summary.current_day}`) : `/planos/${p.slug}`;
            return (
              <Link
                key={p.id}
                to={targetHref}
                data-testid={`plan-card-${p.slug}`}
                className="group relative overflow-hidden rounded-3xl border border-border card-lift"
                style={{ background: p.cover_color || "#0B1E3F" }}
              >
                <div className="absolute inset-0 opacity-[0.08] bg-[radial-gradient(circle_at_85%_15%,hsl(var(--gold))_0%,transparent_45%)]" />
                <div className="relative p-7 text-white flex flex-col h-full min-h-[280px]">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-gold">
                      Plano
                    </span>
                    <div className="flex items-center gap-1.5 text-xs text-white/70">
                      <Calendar className="w-3.5 h-3.5" />
                      {p.duration_days} dias
                    </div>
                  </div>
                  <h3 className="font-display text-2xl mt-6 leading-tight">{p.title}</h3>
                  <p className="text-sm text-white/70 mt-3 line-clamp-2">{p.description}</p>

                  {/* Progress block */}
                  <div className="mt-5" data-testid={`plan-card-progress-${p.slug}`}>
                    {summary.completed && (
                      <div
                        data-testid={`plan-card-completed-label-${p.slug}`}
                        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] text-gold mb-2"
                      >
                        <CheckCircle2 className="w-3 h-3" /> Plano concluído
                      </div>
                    )}
                    <div className="flex items-center justify-between text-[11px] text-white/75">
                      <span data-testid={`plan-card-progress-text-${p.slug}`}>
                        {summary.completed_days_count} de {summary.total_days || p.duration_days} dias concluídos
                      </span>
                      <span className="text-white/55">{summary.percent}%</span>
                    </div>
                    <div className="mt-1.5 h-1 bg-white/10 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gold transition-all duration-500"
                        style={{ width: `${summary.percent}%` }}
                      />
                    </div>
                  </div>

                  <div className="mt-auto pt-5 flex items-center gap-2 text-xs uppercase tracking-widest text-gold">
                    <span data-testid={`plan-card-cta-${p.slug}`}>{ctaLabel(summary)}</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      <div className="mt-10">
        <NewsletterCTA
          interest="plans"
          title="Receba os planos por e-mail"
          subtitle="Avisamos quando novos planos de leitura forem publicados."
          testidPrefix="plans-newsletter"
        />
      </div>
    </div>
  );
}
